import { z } from "zod/v4"

declare class Id<T extends string> {
  private IDENTITY: T
}

// Firestore ドキュメント ID として有効な文字列かどうかを検証するスキーマ
// Firestore の制限:
// - 空文字列は不可
// - `/` を含むことは不可（パス区切り文字として解釈される）
// - `.` または `..` のみは不可
// - `__.*__` にマッチすることは不可（予約済み）
export const FirestoreDocumentIdSchema = z.string()
  .min(1, "IDは空文字列にできません")
  .refine(s => !s.includes('/'), "IDにスラッシュを含むことはできません")
  .refine(s => s !== '.' && s !== '..', "IDは単一または二重ピリオドのみにできません")
  .refine(s => !/^__.*__$/.test(s), "IDは__で囲まれた文字列にできません")

export type DefaultLookId = Id<"DefaultLook"> & string;

export function DefaultLookId(id: string): DefaultLookId {
  return id as DefaultLookId
}

export const DefaultLookIdSchema = FirestoreDocumentIdSchema.describe("デフォルトルックのID").transform(x => DefaultLookId(x))

export type CoinPackId = Id<"CoinPack"> & string;

export function CoinPackId(id: string): CoinPackId {
  return id as CoinPackId
}

export type TaskId = Id<"Task"> & string;

export function TaskId(id: string): TaskId {
  return id as TaskId
}

export type TaskResultId = Id<"TaskResult"> & string;

export function TaskResultId(id: string): TaskResultId {
  return id as TaskResultId
}

export const TaskResultIdSchema = FirestoreDocumentIdSchema.describe("タスク結果ID").transform(TaskResultId)

export type InviteKey = Id<"Invite"> & string;

export function InviteKey(id: string): InviteKey {
  return id as InviteKey
}

export const InviteKeySchema = FirestoreDocumentIdSchema.describe("招待コード").transform(InviteKey)

export type UserId = Id<"User"> & string;

export function UserId(id: string): UserId {
  return id as UserId
}

export const UserIdSchema = FirestoreDocumentIdSchema.describe("ユーザーID").transform(UserId)

export type AdminId = Id<"Admin"> & string;

export function AdminId(id: string): AdminId {
  return id as AdminId
}

export type AdminInviteId = Id<"AdminInvite"> & string;

export function AdminInviteId(id: string): AdminInviteId {
  return id as AdminInviteId
}

export type FriendCode = Id<"FriendCode"> & string;

export function FriendCode(id: string): FriendCode {
  return id as FriendCode
}

export const FriendCodeSchema = FirestoreDocumentIdSchema.describe("フレンドコード").transform(x => FriendCode(x))

export type UserVrmRegistrationId = Id<"UserVrmRegistration"> & string;

export function UserVrmRegistrationId(id: string): UserVrmRegistrationId {
  return id as UserVrmRegistrationId
}

export const UserVrmRegistrationIdSchema = FirestoreDocumentIdSchema.describe("VRM登録ドキュメントのID。ユーザーの vrm-registrations サブコレクションの自動生成ドキュメントIDを指す。").transform(x => UserVrmRegistrationId(x))

export type SoulId = Id<"Soul"> & string;

export function SoulId(id: string): SoulId {
  return id as SoulId
}

export const SoulIdSchema = FirestoreDocumentIdSchema.describe("魂のID。souls コレクションのドキュメントIDを指す。").transform(x => SoulId(x))


export type PredefinedNicknameId = Id<"PredefinedNickname"> & string

export function PredefinedNicknameId(id: string): PredefinedNicknameId {
  return id as PredefinedNicknameId
}

export const PredefinedNicknameIdSchema = FirestoreDocumentIdSchema.describe("ニックネームのID").transform(x => PredefinedNicknameId(x))

export type PredefinedAliasId = Id<"PredefinedAliases"> & string

export function PredefinedAliasId(id: string): PredefinedAliasId {
  return id as PredefinedAliasId
}

export const PredefinedAliasIdSchema = FirestoreDocumentIdSchema.describe("二つ名のID").transform(x => PredefinedAliasId(x))

export type PredefinedAliasQualifierId = Id<"PredefinedAliasQualifier"> & string

export function PredefinedAliasQualifierId(id: string): PredefinedAliasQualifierId {
  return id as PredefinedAliasQualifierId
}

export const PredefinedAliasQualifierIdSchema = FirestoreDocumentIdSchema.describe("二つ名の接頭辞のID").transform(x => PredefinedAliasQualifierId(x))

export const SpaceBGMIdSchema = FirestoreDocumentIdSchema.describe("共通BGMのID").transform(x => SpaceBGMId(x))

export type SpaceBGMId = Id<"SpaceBGM"> & string

export function SpaceBGMId(id: string): SpaceBGMId {
  return id as SpaceBGMId
}

export const SpaceBackgroundIdSchema = FirestoreDocumentIdSchema.describe("共通背景のID").transform(x => SpaceBackgroundId(x))

export type SpaceBackgroundId = Id<"SpaceBackground"> & string

export function SpaceBackgroundId(id: string): SpaceBackgroundId {
  return id as SpaceBackgroundId
}

export type SpaceKey = Id<"SpaceKey"> & string;

export function SpaceKey(id: string): SpaceKey {
  return id as SpaceKey
}

export const SpaceKeySchema = FirestoreDocumentIdSchema.describe("スペースのID").transform(x => SpaceKey(x))

export type SpaceMessageId = Id<"SpaceMessage"> & string;

export function SpaceMessageId(id: string): SpaceMessageId {
  return id as SpaceMessageId
}

export const SpaceMessageIdSchema = FirestoreDocumentIdSchema.describe("スペースのメッセージのID").transform(x => SpaceMessageId(x))

export type LiveGenreId = Id<"Genre"> & string;

export function LiveGenreId(id: string): LiveGenreId {
  return id as LiveGenreId
}

export const LiveGenreIdSchema = FirestoreDocumentIdSchema.describe("ジャンルのID").transform(x => LiveGenreId(x))

export type LiveId = Id<"Live"> & string;

export function LiveId(id: string): LiveId {
  return id as LiveId
}

export const LiveIdSchema = FirestoreDocumentIdSchema.describe("ライブのID").transform(x => LiveId(x))

export type LivePowerId = Id<"LivePower"> & string;

export function LivePowerId(id: string): LivePowerId {
  return id as LivePowerId
}

export const LivePowerIdSchema = FirestoreDocumentIdSchema.describe("ライブのパワーのID").transform(x => LivePowerId(x))

export type LiveCountId = Id<"LiveCount"> & string;

export function LiveCountId(id: string): LiveCountId {
  return id as LiveCountId
}

export const LiveCountIdSchema = FirestoreDocumentIdSchema.describe("ライブの視聴者数情報を持つドキュメントのID").transform(x => LiveCountId(x))

export type LiveCommentId = Id<"LiveComment"> & string;

export function LiveCommentId(id: string): LiveCommentId {
  return id as LiveCommentId
}

export const LiveCommentIdSchema = FirestoreDocumentIdSchema.describe("ライブのコメントのID").transform(x => LiveCommentId(x))

export type LiveReactionId = Id<"LiveReaction"> & string;

export function LiveReactionId(id: string): LiveReactionId {
  return id as LiveReactionId
}

export const LiveReactionIdSchema = FirestoreDocumentIdSchema.describe("ライブのリアクションのID").transform(x => LiveReactionId(x))

export type LiveFreeBoardId = Id<"LiveFreeBoard"> & string;

export function LiveFreeBoardId(id: string): LiveFreeBoardId {
  return id as LiveFreeBoardId
}

export const LiveFreeBoardIdSchema = FirestoreDocumentIdSchema.describe("ライブのフリーボードのID").transform(x => LiveFreeBoardId(x))

export type LiveToken = Id<"LiveToken"> & string;

export function LiveToken(id: string): LiveToken {
  return id as LiveToken
}

export const LiveTokenSchema = FirestoreDocumentIdSchema.describe("ライブ視聴のURLを構成するトークン。このトークンを知っているユーザーはライブを実質的に視聴できる。").transform(x => LiveToken(x))

export type StreamName = Id<"Stream"> & string;

export function StreamName(spaceKey: SpaceKey, liveId: LiveId, token: LiveToken): StreamName {
  return spaceKey + "_" + liveId + "_" + token as StreamName
}
export function StreamNameByString(id: string): StreamName {
  return id as StreamName
}

export const StreamNameSchema = FirestoreDocumentIdSchema.describe("ライブ視聴のURLを構成する文字列。この文字列を知っているユーザーはライブを実質的に視聴できる。").transform(x => StreamNameByString(x))

export type LiveTaskId = Id<"LiveTask"> & string;

export function LiveTaskId(id: string): LiveTaskId {
  return id as LiveTaskId
}

export const LiveTaskIdSchema = FirestoreDocumentIdSchema.describe("TRTC-CDNのストリーム処理の実行タスクのID").transform(x => LiveTaskId(x))

export type SelectionId = Id<"Selection"> & string;

export function SelectionId(id: string): SelectionId {
  return id as SelectionId
}

export const SelectionIdSchema = FirestoreDocumentIdSchema.describe("投稿募集のID").transform(x => SelectionId(x))

export type SelectionSequenceId = Id<"SelectionSequence"> & string;

export function SelectionSequenceId(id: string): SelectionSequenceId {
  return id as SelectionSequenceId
}

export const SelectionSequenceIdSchema = FirestoreDocumentIdSchema.describe("投稿募集のシーケンスのID").transform(x => SelectionSequenceId(x))

export type SelectionParticipantId = Id<"SelectionParticipant"> & string;

export function SelectionParticipantId(id: string): SelectionParticipantId {
  return id as SelectionParticipantId
}

export const SelectionParticipantIdSchema = FirestoreDocumentIdSchema.describe("投稿募集の応募のID").transform(x => SelectionParticipantId(x))

export type BrandId = Id<'Brand'> & string

export function BrandId(id: string): BrandId {
  return id as BrandId;
}

export const BrandIdSchema = FirestoreDocumentIdSchema.describe("ブランドのID").transform(x => BrandId(x))

export type ItemDistributionId = Id<'ItemDistribution'> & string

export function ItemDistributionId(id: string): ItemDistributionId {
  return id as ItemDistributionId;
}

export const ItemDistributionIdSchema = FirestoreDocumentIdSchema.describe("販売のID").transform(x => ItemDistributionId(x))

export type SaleId = Id<'Sale'> & string

export function SaleId(id: string): SaleId {
  return id as SaleId;
}

export const SaleIdSchema = FirestoreDocumentIdSchema.describe("セールのID").transform(x => SaleId(x))

export type ItemId = Id<'Item'> & string

export function ItemId(id: string): ItemId {
  return id as ItemId;
}

export const ItemIdSchema = FirestoreDocumentIdSchema.describe("アイテムのID").transform(x => ItemId(x))

export type PowerId = Id<'Power'> & string

export function PowerId(id: string): PowerId {
  return id as PowerId
}

export const PowerIdSchema = FirestoreDocumentIdSchema.describe("パワーのID").transform(x => PowerId(x))

export type ProductId = Id<'Product'> & string

export function ProductId(id: string): ProductId {
  return id as ProductId
}

export type CoinPurchaseId = Id<"CoinPurchase"> & string

export function CoinPurchaseId(id: string): CoinPurchaseId {
  return id as CoinPurchaseId
}

export type GlobalNgWordId = Id<"GlobalNgWord"> & string

export function GlobalNgWordId(id: string): GlobalNgWordId {
  return id as GlobalNgWordId
}

export const GlobalNgWordIdSchema = FirestoreDocumentIdSchema.describe("NGワードのID").transform(x => GlobalNgWordId(x))

export type AllowedDomainId= Id<"AllowedDomain"> & string

export function AllowedDomainId(id: string): AllowedDomainId {
  return id as AllowedDomainId
}


export type CallPushId= Id<"CallPush"> & string

export function CallPushId(id: string): CallPushId {
  return id as CallPushId
}

export const CallPushIdSchema = FirestoreDocumentIdSchema.describe("コールのID").transform(x => CallPushId(x))

export type PushDeviceName = Id<"PushDevice"> & string

export function PushDeviceName(id: string): PushDeviceName {
  return id as PushDeviceName
}

export const PushDeviceNameSchema = FirestoreDocumentIdSchema.describe("ユーザーのデバイス名（ID）").transform(x => PushDeviceName(x))

export type ViolationReportId = Id<"ViolationReport"> & string;

export function ViolationReportId(id: string): ViolationReportId {
  return id as ViolationReportId
}

export const ViolationReportIdSchema = FirestoreDocumentIdSchema.describe("通報ID").transform(ViolationReportId)

export type TopicSuggestionId = Id<"TopicSuggestion"> & string;

export function TopicSuggestionId(id: string): TopicSuggestionId {
  return id as TopicSuggestionId
}

export const TopicSuggestionIdSchema = FirestoreDocumentIdSchema.describe("話題のお題のID").transform(x => TopicSuggestionId(x))

export type SystemNotificationId= Id<"SystemNotification"> & string;

export function SystemNotificationId(id: string): SystemNotificationId {
  return id as SystemNotificationId
}

export const SystemNotificationIdSchema = FirestoreDocumentIdSchema.describe("運営からのお知らせのID").transform(x => SystemNotificationId(x))

export type ShopBannerId = Id<"ShopBanner"> & string

export function ShopBannerId(id: string): ShopBannerId {
  return id as ShopBannerId
}
export type ShopCustomTagId = Id<"ShopCustomTag"> & string;

export function ShopCustomTagId(id: string): ShopCustomTagId {
  return id as ShopCustomTagId
}

export const ShopCustomTagIdSchema = FirestoreDocumentIdSchema.describe("ショップカスタムタグのID").transform(x => ShopCustomTagId(x))

export const ShopBannerIdSchema = FirestoreDocumentIdSchema.describe("ショップバナーのID").transform(x => ShopBannerId(x))

export type StoryId = Id<"Story"> & string;

export function StoryId(id: string): StoryId {
  return id as StoryId
}

export const StoryIdSchema = FirestoreDocumentIdSchema.describe("ストーリーのID").transform(x => StoryId(x))

export type ManagedImageId = Id<"ManagedImage"> & string;

export function ManagedImageId(id: string): ManagedImageId {
  return id as ManagedImageId
}

export const ManagedImageIdSchema = FirestoreDocumentIdSchema.describe("画像のID").transform(x => ManagedImageId(x))

export type HomeBannerId = Id<"HomeBanner"> & string;

export function HomeBannerId(id: string): HomeBannerId {
  return id as HomeBannerId
}

export const HomeBannerIdSchema = FirestoreDocumentIdSchema.describe("ホームバナーのID").transform(x => HomeBannerId(x))

export type BulkDeliveryMasterId = Id<"BulkDeliveryMaster"> & string;

export function BulkDeliveryMasterId(id: string): BulkDeliveryMasterId {
  return id as BulkDeliveryMasterId
}

export const BulkDeliveryMasterIdSchema = FirestoreDocumentIdSchema.describe("一括配信マスターのID").transform(x => BulkDeliveryMasterId(x))

export type BulkDeliveryTargetChunkId = Id<"BulkDeliveryTargetChunk"> & string;

export function BulkDeliveryTargetChunkId(id: string): BulkDeliveryTargetChunkId {
  return id as BulkDeliveryTargetChunkId
}

export const BulkDeliveryTargetChunkIdSchema = FirestoreDocumentIdSchema.describe("一括配信対象チャンクのID").transform(x => BulkDeliveryTargetChunkId(x))

export type WelcomeDeliveryMasterId = Id<"WelcomeDeliveryMaster"> & string;

export function WelcomeDeliveryMasterId(id: string): WelcomeDeliveryMasterId {
  return id as WelcomeDeliveryMasterId
}

export const WelcomeDeliveryMasterIdSchema = FirestoreDocumentIdSchema.describe("新規ユーザー配信マスターのID").transform(x => WelcomeDeliveryMasterId(x))

export type PersonalNotificationId = Id<"PersonalNotification"> & string;

export function PersonalNotificationId(id: string): PersonalNotificationId {
  return id as PersonalNotificationId
}

export const PersonalNotificationIdSchema = FirestoreDocumentIdSchema.describe("個人通知のID").transform(x => PersonalNotificationId(x))

export type MaintenanceScheduleEntryId = Id<"MaintenanceScheduleEntry"> & string

export function MaintenanceScheduleEntryId(id: string): MaintenanceScheduleEntryId {
  return id as MaintenanceScheduleEntryId
}

export const MaintenanceScheduleEntryIdSchema = FirestoreDocumentIdSchema.describe("メンテナンススケジュールエントリのID").transform(x => MaintenanceScheduleEntryId(x))

export type StampCardId = Id<"StampCard"> & string

export function StampCardId(id: string): StampCardId {
  return id as StampCardId
}

export const StampCardIdSchema = FirestoreDocumentIdSchema.describe("スタンプカードのID").transform(x => StampCardId(x))

export type SubscriptionHistoryId = Id<"SubscriptionHistory"> & string

export function SubscriptionHistoryId(id: string): SubscriptionHistoryId {
  return id as SubscriptionHistoryId
}

export const SubscriptionHistoryIdSchema = FirestoreDocumentIdSchema.describe("サブスクリプション履歴のID").transform(x => SubscriptionHistoryId(x))

export type SubscriptionPlanId = Id<"SubscriptionPlan"> & string

export function SubscriptionPlanId(id: string): SubscriptionPlanId {
  return id as SubscriptionPlanId
}

export const SubscriptionPlanIdSchema = FirestoreDocumentIdSchema.describe("定期購入プランのID").transform(x => SubscriptionPlanId(x))

export type SubscriptionProductId = Id<"SubscriptionProduct"> & string

export function SubscriptionProductId(id: string): SubscriptionProductId {
  return id as SubscriptionProductId
}

export const SubscriptionProductIdSchema = FirestoreDocumentIdSchema.describe("定期購入商品のID").transform(x => SubscriptionProductId(x))

export type StampLaneId = Id<"StampLane"> & string

export function StampLaneId(id: string): StampLaneId {
  return id as StampLaneId
}

export const StampLaneIdSchema = FirestoreDocumentIdSchema.describe("スタンプレーンのID").transform(x => StampLaneId(x))

export type StampLaneRewardId = Id<"StampLaneReward"> & string

export function StampLaneRewardId(id: string): StampLaneRewardId {
  return id as StampLaneRewardId
}

export const StampLaneRewardIdSchema = FirestoreDocumentIdSchema.describe("スタンプレーン報酬のID").transform(x => StampLaneRewardId(x))

export type StampMissionId = Id<"StampMission"> & string

export function StampMissionId(id: string): StampMissionId {
  return id as StampMissionId
}

export const StampMissionIdSchema = FirestoreDocumentIdSchema.describe("スタンプミッションのID").transform(x => StampMissionId(x))

export type DeviceId = Id<"Device"> & string

export function DeviceId(id: string): DeviceId {
  return id as DeviceId
}

export const DeviceIdSchema = FirestoreDocumentIdSchema.describe("デバイスID").transform(x => DeviceId(x))

export type SubscriptionId = Id<"Subscription"> & string

export function SubscriptionId(id: string): SubscriptionId {
  return id as SubscriptionId
}

export const SubscriptionIdSchema = FirestoreDocumentIdSchema.describe("Iサブスクリプション契約のID").transform(x => SubscriptionId(x))

export type PinnedStoryUserId = Id<"PinnedStoryUser"> & string

export function PinnedStoryUserId(id: string): PinnedStoryUserId {
  return id as PinnedStoryUserId
}

export const PinnedStoryUserIdSchema = FirestoreDocumentIdSchema.describe("ストーリーズピン留めのID").transform(x => PinnedStoryUserId(x))

export type HomePickupId = Id<"HomePickup"> & string

export function HomePickupId(id: string): HomePickupId {
  return id as HomePickupId
}

export const HomePickupIdSchema = FirestoreDocumentIdSchema.describe("ホームピックアップのID").transform(x => HomePickupId(x))

export type SubscriptionItemGrantMasterId = Id<"SubscriptionItemGrantMaster"> & string

export function SubscriptionItemGrantMasterId(id: string): SubscriptionItemGrantMasterId {
  return id as SubscriptionItemGrantMasterId
}

export const SubscriptionItemGrantMasterIdSchema = FirestoreDocumentIdSchema.describe("サブスクリプションアイテム配布マスターのID").transform(x => SubscriptionItemGrantMasterId(x))

export type SubscriptionItemGrantDistributionChunkId = Id<"SubscriptionItemGrantDistributionChunk"> & string

export function SubscriptionItemGrantDistributionChunkId(id: string): SubscriptionItemGrantDistributionChunkId {
  return id as SubscriptionItemGrantDistributionChunkId
}

export const SubscriptionItemGrantDistributionChunkIdSchema = FirestoreDocumentIdSchema.describe("サブスクリプションアイテム配布チャンクのID").transform(x => SubscriptionItemGrantDistributionChunkId(x))

export type HomeLivePinId = Id<"HomeLivePin"> & string

export function HomeLivePinId(id: string): HomeLivePinId {
  return id as HomeLivePinId
}

export const HomeLivePinIdSchema = FirestoreDocumentIdSchema.describe("LIVEピン留めのID").transform(x => HomeLivePinId(x))

export type AdminOperationId = Id<"AdminOperation"> & string

export function AdminOperationId(id: string): AdminOperationId {
  return id as AdminOperationId
}

export const AdminOperationIdSchema = FirestoreDocumentIdSchema.describe("管理者操作ログのID").transform(x => AdminOperationId(x))

export type VoiceId = Id<"Voice"> & string

export function VoiceId(id: string): VoiceId {
  return id as VoiceId
}

export const VoiceIdSchema = FirestoreDocumentIdSchema.describe("ボイス投稿のID").transform(x => VoiceId(x))

export type VoiceReplyId = Id<"VoiceReply"> & string

export function VoiceReplyId(id: string): VoiceReplyId {
  return id as VoiceReplyId
}

export const VoiceReplyIdSchema = FirestoreDocumentIdSchema.describe("ボイス返信のID").transform(x => VoiceReplyId(x))

export type VoiceEmoteId = Id<"VoiceEmote"> & string

export function VoiceEmoteId(id: string): VoiceEmoteId {
  return id as VoiceEmoteId
}

export const VoiceEmoteIdSchema = FirestoreDocumentIdSchema.describe("ボイスエモートのID").transform(x => VoiceEmoteId(x))

export type VoiceReplyEmoteId = Id<"VoiceReplyEmote"> & string

export function VoiceReplyEmoteId(id: string): VoiceReplyEmoteId {
  return id as VoiceReplyEmoteId
}

export const VoiceReplyEmoteIdSchema = FirestoreDocumentIdSchema.describe("ボイス返信エモートのID").transform(x => VoiceReplyEmoteId(x))

export type LivePointId = Id<"LivePoint"> & string

export function LivePointId(id: string): LivePointId {
  return id as LivePointId
}

export const LivePointIdSchema = FirestoreDocumentIdSchema.describe("LIVEポイントドキュメントのID").transform(x => LivePointId(x))
